import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, eur, catLabel, gr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";
import { MyDataCard } from "@/components/MyDataCard";
import { AuditCard } from "@/components/AuditCard";

export default function Settings({ config }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: boats = [] } = useQuery({ queryKey: ["boats"], queryFn: async () => (await api.get("/boats")).data });

  if (!config) {
    return (
      <div>
        <PageHeader title="Ρυθμίσεις εγκατάστασης - τιμολόγησης" />
        <div className="p-6">
          <div className="rounded-sm border border-dashed border-border p-10 text-center" data-testid="settings-empty">
            <div className="text-sm text-muted-foreground">Δεν έχουν οριστεί τα στοιχεία της εγκατάστασής σας και η τιμολόγηση.</div>
            <Button className="mt-4 rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => navigate("/setup")} data-testid="settings-setup-btn">Ρύθμιση στοιχείων εγκατάστασης</Button>
          </div>
        </div>
      </div>
    );
  }

  const priceFor = (catId, perId) => {
    const p = config.prices[`${catId}|${perId}`];
    if (p && typeof p === "object") return p;
    return { monthly: 0 };
  };

  return (
    <div>
      <PageHeader
        title="Ρυθμίσεις εγκατάστασης - τιμολόγησης"
        subtitle={config.name}
        action={
          <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => navigate("/setup")} data-testid="edit-config-btn">
            <PencilSimple size={18} weight="bold" /> Επεξεργασία
          </Button>
        }
      />
      <div className="space-y-6 p-6">
        <Card className="rounded-sm border-border shadow-none">
          <div className="border-b border-border px-6 py-4">
            <h3 className="font-head font-bold">Τιμοκατάλογος</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pricing-table">
              <thead>
                <tr className="border-b border-border text-left text-xs tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Κατηγορία</th>
                  <th className="px-4 py-3 font-semibold">Μήκος</th>
                  {config.periods.map((p) => (
                    <th key={p.id} className="px-4 py-3 text-right font-semibold">
                      <div>{p.name}</div>
                      {p.start_date && <div className="text-[11px] font-normal normal-case text-muted-foreground">{gr(p.start_date)} – {gr(p.end_date)}</div>}
                      <div className="text-[11px] font-normal normal-case text-muted-foreground">Μηνιαία χρέωση</div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right font-semibold">Ετήσια</th>
                </tr>
              </thead>
              <tbody>
                {config.categories.map((c) => (
                  <tr key={c.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium">{catLabel(c.name)}{c.per_meter && <span className="ml-2 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[10px] font-semibold text-[#0055FF]">ανά μέτρο</span>}</td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">{c.min_length}–{c.max_length} m</td>
                    {config.periods.map((p) => {
                      const pr = priceFor(c.id, p.id);
                      return (
                        <td key={p.id} className="px-4 py-3 text-right tabular-nums">
                          <div>{eur(pr.monthly)}{c.per_meter ? "/μ" : ""}</div>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">{eur(c.yearly)}{c.per_meter ? "/μ" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <MyDataCard />
        <AuditCard />

        {boats.length > 0 && (
          <Card className="rounded-sm border-amber-200 bg-amber-50 shadow-none">
            <div className="p-4 text-sm text-amber-800">
              Προσοχή: Υπάρχουν {boats.length} καταχωρημένα σκάφη. Η επανεπεξεργασία των θέσεων ενδέχεται να επηρεάσει τις υπάρχουσες αναθέσεις.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
